import FormHeader from "@/components/FormHeader";
import InputError from "@/components/InputError";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTahun } from "@/hooks/useTahun";
import api from "@/lib/axios";
import { getListPeriode } from "@/services/getData";
import { Periode } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

type InputState = {
  source_id_sys_periode: string;
  target_id_sys_periode: string;
};

const postData = async (newData: InputState) => {
  const response = await api.post("/setting/sys-prodi/copy-mapping", newData);
  return response.data;
};

export default function FormCopyMapping({
  closeModal,
}: {
  closeModal: () => void;
}) {
  const [searchParams] = useSearchParams();
  const id_sys_periode = searchParams.get("id") || "";

  const [form, setForm] = useState<InputState>({
    source_id_sys_periode: "",
    target_id_sys_periode: id_sys_periode || "",
  });

  const { selectedTahun } = useTahun();

  const { data: dataPeriode } = useQuery({
    queryKey: ["listPeriode"],
    queryFn: () => getListPeriode(selectedTahun?.label),
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: postData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["periode"] });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const handleCopyMapping = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate(form);
  };

  return (
    <form onSubmit={handleCopyMapping}>
      <FormHeader
        title="Copy Mapping Prodi x Jurusan Sekolah"
        icon={<></>}
        description="Apakah anda yakin akan melakukan Copy Mapping Prodi x Jurusan Sekolah?"
      />
      <div>
        <div>
          <Label>Source Periode</Label>
          <Select
            required
            value={form.source_id_sys_periode}
            onValueChange={(value) => {
              if (!value) return;
              setForm((prev) => ({ ...prev, source_id_sys_periode: value }));
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={"Pilih Periode"} />
            </SelectTrigger>
            <SelectContent>
              {dataPeriode?.map((periode: Periode) => (
                <SelectItem
                  key={periode.id_sys_periode}
                  value={`${periode.id_sys_periode}`}
                >
                  {periode.tahun} - {periode.jalur_jenis?.nama_jalur_jenis} -{" "}
                  Gelombang {periode.gelombang}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <InputError
            text={error?.response?.data?.errors?.source_id_sys_periode?.join(
              ",",
            )}
          />
        </div>
        <div className="mt-5 flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            onClick={closeModal}
            disabled={isPending}
            className="flex-1"
          >
            Batal
          </Button>
          <Button
            className="flex-1 flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Copy"
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}
